import React, { useMemo } from 'react';
import * as Collapsible from '@radix-ui/react-collapsible';
import Icon from '@/components/ui/Icon';
import cn from '@/utils/cn';
import { SPANavSlot } from '../shared/SPANav';
import { buildFileTree, getFileLabel } from './devDocs.utils';
import type { DocFile } from './devDocs.utils';

interface DevDocsSidebarProps {
  files: DocFile[];
  onSelectFile: (path: string) => void;
  searchQuery: string;
  selectedPath: string;
  onSearchChange: (query: string) => void;
}

const fileButtonClass = (isActive: boolean) => cn(
  'tw-w-full tw-text-left tw-px-3 tw-py-1.5 tw-rounded-md tw-text-sm tw-truncate',
  'tw-border-0 tw-cursor-pointer tw-block',
  isActive
    ? 'tw-bg-spa-accent-bg tw-text-spa-accent-text-hover tw-font-medium'
    : 'tw-bg-transparent tw-text-spa-primary hover:tw-bg-spa-bg-tertiary',
);

const DevDocsSidebar = ({ files, onSearchChange, onSelectFile, searchQuery, selectedPath }: DevDocsSidebarProps) => {
  const { rootFiles, sections } = useMemo(() => buildFileTree(files), [files]);

  const matchesQuery = (f: DocFile, query: string) => {
    const q = query.toLowerCase();
    return f.path.toLowerCase().includes(q)
      || getFileLabel(f).toLowerCase().includes(q);
  };

  const filteredRootFiles = useMemo(
    () => (searchQuery
      ? rootFiles.filter(f => matchesQuery(f, searchQuery))
      : rootFiles),
    [rootFiles, searchQuery],
  );

  const filteredSections = useMemo(
    () => sections.map(section => ({
      ...section,
      files: searchQuery
        ? section.files.filter(f => matchesQuery(f, searchQuery))
        : section.files,
      subdirectories: section.subdirectories
        .map(subdir => ({
          ...subdir,
          files: searchQuery
            ? subdir.files.filter(f => matchesQuery(f, searchQuery))
            : subdir.files,
        }))
        .filter(subdir => !searchQuery || subdir.files.length > 0),
    })).filter(section => !searchQuery || section.files.length > 0 || section.subdirectories.length > 0),
    [sections, searchQuery],
  );

  const isSearching = searchQuery.length > 0;

  const search = (
    <input
      className={cn(
        'tw-w-full tw-box-border tw-px-3 tw-py-2 tw-text-sm tw-rounded-md',
        'tw-border tw-border-solid tw-border-spa-border-input tw-bg-spa-bg-tertiary tw-text-spa-on-bg',
        'tw-outline-none tw-placeholder-spa-secondary',
        'focus:tw-border-spa-accent focus:tw-ring-1 focus:tw-ring-spa-accent',
      )}
      placeholder="Search files..."
      type="text"
      value={searchQuery}
      onChange={e => onSearchChange(e.target.value)}
    />
  );

  const tree = (
    <nav className="tw-px-2 tw-pt-1 tw-pb-3">
      {filteredRootFiles.map(file => (
        <button
          key={file.path}
          className={fileButtonClass(selectedPath === file.path)}
          type="button"
          onClick={() => onSelectFile(file.path)}
        >
          {getFileLabel(file)}
        </button>
      ))}

      {filteredSections.map(section => (
        <Collapsible.Root key={`${section.label}-${isSearching}`} defaultOpen>
          <Collapsible.Trigger
            className={cn(
              'tw-w-full tw-flex tw-items-center tw-justify-between tw-px-3 tw-py-2 tw-mt-2',
              'tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider tw-text-spa-secondary',
              'tw-border-0 tw-bg-transparent tw-cursor-pointer tw-rounded-md hover:tw-bg-spa-bg-tertiary',
            )}
          >
            {section.label}
            <Icon icon="ChevronDown" size={14} />
          </Collapsible.Trigger>
          <Collapsible.CollapsibleContent className="CollapsibleContent">
            <div className="tw-ml-1">
              {section.files.map(file => (
                <button
                  key={file.path}
                  className={fileButtonClass(selectedPath === file.path)}
                  type="button"
                  onClick={() => onSelectFile(file.path)}
                >
                  {getFileLabel(file)}
                </button>
              ))}

              {section.subdirectories.map(subdir => (
                <Collapsible.Root key={`${subdir.label}-${isSearching}`} defaultOpen>
                  <Collapsible.Trigger
                    className={cn(
                      'tw-w-full tw-flex tw-items-center tw-justify-between tw-px-3 tw-py-1.5',
                      'tw-text-xs tw-font-medium tw-text-spa-secondary',
                      'tw-border-0 tw-bg-transparent tw-cursor-pointer tw-rounded-md hover:tw-bg-spa-bg-tertiary',
                    )}
                  >
                    {subdir.label}
                    <Icon icon="ChevronDown" size={12} />
                  </Collapsible.Trigger>
                  <Collapsible.CollapsibleContent className="CollapsibleContent">
                    <div className="tw-ml-2">
                      {subdir.files.map(file => (
                        <button
                          key={file.path}
                          className={fileButtonClass(selectedPath === file.path)}
                          type="button"
                          onClick={() => onSelectFile(file.path)}
                        >
                          {getFileLabel(file)}
                        </button>
                      ))}
                    </div>
                  </Collapsible.CollapsibleContent>
                </Collapsible.Root>
              ))}
            </div>
          </Collapsible.CollapsibleContent>
        </Collapsible.Root>
      ))}
    </nav>
  );

  return (
    <SPANavSlot header={{ backLabel: 'Dev', backTo: '/dev', title: 'DevDocs' }}>
      <div className="tw-box-border tw-p-3 tw-pb-1">{search}</div>
      {tree}
    </SPANavSlot>
  );
};

export default DevDocsSidebar;
